import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Amount, useAmountVisibility } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReportsPage = () => {
  const { hidden: amountsHidden } = useAmountVisibility();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [combinedReport, setCombinedReport] = useState(null);
  const [salaryReport, setSalaryReport] = useState(null);
  const [expenseReport, setExpenseReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReports();
  }, [month]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [combinedRes, salaryRes, expenseRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/combined?month=${month}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/salary?month=${month}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/reports/expenses?month=${month}`, { withCredentials: true }),
      ]);
      setCombinedReport(combinedRes.data);
      setSalaryReport(salaryRes.data);
      setExpenseReport(expenseRes.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = combinedReport
    ? [
        {
          name: 'Monthly Overview',
          Salary: combinedReport.total_salary,
          Expenses: combinedReport.total_expenses,
        },
      ]
    : [];

  return (
    <div data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Reports
        </h1>
        <p className="text-[#52525B] text-sm">View salary, expenses, and combined reports</p>
      </div>

      <div className="mb-6">
        <Label>Select Month</Label>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-64" data-testid="report-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date();
              d.setMonth(d.getMonth() - i);
              return d.toISOString().slice(0, 7);
            }).map((m) => (
              <SelectItem key={m} value={m}>
                {new Date(m).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-lg text-[#52525B]">Loading reports...</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card className="p-6 border border-[#E4E4E7]">
              <p className="text-xs uppercase tracking-wider text-[#52525B] font-semibold mb-2">Total Salary Paid</p>
              <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: '#1D9E75' }}>
                <Amount value={combinedReport?.total_salary || 0} format={(n) => Number(n).toLocaleString()} />
              </p>
              <p className="text-xs text-[#52525B] mt-2">{salaryReport?.count || 0} employees</p>
            </Card>

            <Card className="p-6 border border-[#E4E4E7]">
              <p className="text-xs uppercase tracking-wider text-[#52525B] font-semibold mb-2">Total Expenses</p>
              <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: '#DC2626' }}>
                <Amount value={combinedReport?.total_expenses || 0} format={(n) => Number(n).toLocaleString()} />
              </p>
              <p className="text-xs text-[#52525B] mt-2">{expenseReport?.expenses?.length || 0} transactions</p>
            </Card>

            <Card className="p-6 border border-[#E4E4E7]">
              <p className="text-xs uppercase tracking-wider text-[#52525B] font-semibold mb-2">Grand Total</p>
              <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: '#16A34A' }}>
                <Amount value={combinedReport?.grand_total || 0} format={(n) => Number(n).toLocaleString()} />
              </p>
              <p className="text-xs text-[#52525B] mt-2">Salary + Expenses</p>
            </Card>
          </div>

          <Card className="p-6 border border-[#E4E4E7] mb-6">
            <h3 className="text-lg font-semibold mb-6">Monthly Overview</h3>
            {amountsHidden ? (
              <div className="h-[300px] flex flex-col items-center justify-center bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-400">
                <div className="text-3xl">🔒</div>
                <div className="text-sm mt-1.5 font-medium">Chart hidden</div>
                <div className="text-[11px] mt-0.5">Click the eye icon in the header to show</div>
              </div>
            ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Salary" fill="#1D9E75" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill="#52525B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </Card>

          {expenseReport?.by_category && Object.keys(expenseReport.by_category).length > 0 && (
            <Card className="p-6 border border-[#E4E4E7]">
              <h3 className="text-lg font-semibold mb-4">Expense Breakdown by Category</h3>
              <div className="space-y-3">
                {Object.entries(expenseReport.by_category).map(([category, amount]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{category}</span>
                    <span className="text-sm font-semibold"><Amount value={amount} format={(n) => Number(n).toLocaleString()} /></span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
